import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * VideoHero component using Cloudflare Stream iframe for background video
 * 
 * @param {Object} props Component props
 * @param {string} props.videoId Cloudflare Stream video ID
 * @param {string} props.fallbackImage Fallback image URL
 * @param {string} props.alt Alt text for accessibility
 * @param {React.ReactNode} props.children Content to overlay on video
 * @param {string} props.className Additional CSS classes
 * @param {boolean} props.enableMobileVideo Enable video on mobile devices (default: false)
 * @returns {React.ReactElement} VideoHeroIframe component
 */
const VideoHeroIframe = ({ 
  videoId, 
  fallbackImage, 
  alt, 
  children, 
  className = '',
  customerId = 'customer-b6dep35w7xo2ew5y',
  enableMobileVideo = false
}) => {
  const [showVideo, setShowVideo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const isMobileDevice = width < 768;
      const isTabletDevice = width >= 768 && width < 1024;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      // Check for mobile data saver or slow connection
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const isSlowConnection = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
      
      console.log('VideoHeroIframe Debug:', {
        isMobileDevice,
        isTabletDevice,
        prefersReducedMotion,
        isSlowConnection,
        videoId,
        windowWidth: width,
        connectionType: connection?.effectiveType
      });
      
      setIsMobile(isMobileDevice);
      
      // Enable video based on device and settings
      const shouldShowVideo = (
        (!isMobileDevice || enableMobileVideo) && 
        !prefersReducedMotion && 
        !isSlowConnection
      );
      
      if (shouldShowVideo) {
        setShowVideo(true);
        console.log('Setting showVideo to true for iframe');
      } else {
        console.log('Video disabled - mobile (unless enabled), reduced motion, or slow connection');
        setShowVideo(false);
      }
    };

    checkDevice();
    
    // Re-check on window resize
    const handleResize = () => {
      checkDevice();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [videoId, enableMobileVideo]);

  // Construct Cloudflare Stream iframe URL with mobile-optimized settings
  const mobileParams = isMobile ? '&preload=none&quality=auto' : '';
  const iframeUrl = `https://${customerId}.cloudflarestream.com/${videoId}/iframe?muted=true&autoplay=true&loop=true&controls=false&poster=https%3A%2F%2F${customerId}.cloudflarestream.com%2F${videoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D5s%26height%3D1080${mobileParams}`;


  return (
    <div className={`relative w-full bg-brand-black overflow-hidden ${className}`}>
      {/* Video Background via Iframe */}
      {showVideo && (
        <div className="absolute inset-0 w-full h-full">
          <iframe
            src={iframeUrl}
            className="w-full h-full"
            style={{
              border: 'none',
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              pointerEvents: 'none' // Prevent interaction with video controls
            }}
            allow="autoplay; fullscreen"
            title="Background Video"
          />
        </div>
      )}

      {/* Fallback Image - Only for mobile or when video is disabled */}
      {!showVideo && (
        <div className="absolute inset-0 w-full h-full">
          <img 
            src={fallbackImage}
            alt={alt}
            className="w-full h-full object-cover"
            style={{ 
              height: isMobile ? '100%' : '120%',
              top: '0',
              objectPosition: isMobile ? '50% 30%' : '50% 0%'
            }}
            loading="eager"
            decoding="sync"
          />
        </div>
      )}

      {/* Overlay Gradients */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-black/80 via-brand-black/30 to-brand-black/80 z-10"></div>
      {/* Mobile-specific overlay for better text readability */}
      <div className={`absolute inset-0 z-10 ${isMobile ? 'bg-brand-black/40' : 'bg-brand-black/20 md:hidden'}`}></div>
      
      {/* Content Overlay */}
      <div className="absolute inset-0 z-20">
        {children}
      </div>
    </div>
  );
};

VideoHeroIframe.propTypes = {
  videoId: PropTypes.string.isRequired,
  fallbackImage: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  customerId: PropTypes.string,
  enableMobileVideo: PropTypes.bool
};

export default VideoHeroIframe;
